import { useMutation } from '@apollo/client'
import ActionButton from '@deltek/specification-client/core/Buttons/ActionButton'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import {
    ListingDetailImageInput,
    ListingDetailVideoInput,
} from '@deltek/specification-client/core/Models/productListingDetail'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { PickerResponse } from '@deltek/specification-client/core/Widgets/Filestack/FilestackButton'
import ImageFilestackButton from '@deltek/specification-client/core/Widgets/Filestack/ImageFilestackButton'
import { ProductListingDetailConsolidatedProductMedia as ConsolidatedProductMedia } from '@deltek/specification-client/core/Widgets/ProductListing/Details/Media/productListingDetailConsolidatedProductMedia'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import IconButton from '@material-ui/core/IconButton'
import Tab from '@material-ui/core/Tab'
import Tabs from '@material-ui/core/Tabs'
import { Theme, makeStyles } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import { processEnv } from 'components/configuration/environmentVariables'
import * as filestack from 'filestack-js'
import React, { useEffect, useState } from 'react'
import { baseActionButtonStyles } from '../BlueActionButton'
import GalleryTabContent from './GalleryTabContent'
import PhotosTabContent, { photosFilestackDivId } from './PhotosTabContent'
import {
    PhotosVideosGalleryEnum,
    PhotosVideosGalleryTabPanel,
    a11yProps,
} from './PhotosVideosGalleryTabPanel'
import VideosTabContent, { VideoUpdateProps } from './VideosTabContent'
import useVimeoThumnbailUrl from './useVimeoThumbnail'

const mediaDialogStyles = makeStyles((theme: Theme) => ({
    dialogCloseButton: {
        position: 'absolute',
        top: theme.spacing(0.5),
        right: theme.spacing(0.5),
    },
    dialogActions: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        paddingRight: theme.spacing(3),
    },
    tabs: {
        fontSize: pxToRem(18),
        fontWeight: 500,
        color: '#5e5f61',
        minWidth: 'unset',
        width: 100,
        '&:hover': {
            color: '#13B5EA',
        },
        '&>span': {
            paddingBottom: 4,
        },
        '&.Mui-selected': {
            color: '#13B5EA',
            '&>span': {
                paddingBottom: 'unset',
                borderBottom: '4px solid #13B5EA',
            },
        },
    },
}))

export interface MediaDialogProps {
    initialConsolidatedProductMedia: Record<string, ConsolidatedProductMedia>
    onClose: VoidFunction
    open: boolean
    setInitialConsolidatedProductMedia: (
        newMedia: Record<string, ConsolidatedProductMedia>
    ) => void
    handleMedia: (
        images: Partial<ListingDetailImageInput>[],
        videos: Partial<ListingDetailVideoInput>[]
    ) => void
}

export const MediaDialog: React.FC<MediaDialogProps> = ({
    initialConsolidatedProductMedia,
    onClose,
    open,
    setInitialConsolidatedProductMedia,
    handleMedia,
}) => {
    const baseActionButtonClasses = baseActionButtonStyles()
    const classes = mediaDialogStyles()
    const [activeTab, setActiveTab] = useState<PhotosVideosGalleryEnum>(
        PhotosVideosGalleryEnum.Photos
    )
    const [filestackActive, setFilestackActive] = useState<boolean>(false)
    const [filestackLoading, setFilestackLoading] = useState<boolean>(false)
    const [filestackResponseLoading, setFilestackResponseLoading] = useState<
        boolean
    >(false)
    const [filestackResponse, setFilestackResponse] = useState<PickerResponse>()
    const [filestackPickerInstance, setFilestackPickerInstance] = useState<
        filestack.PickerInstance
    >()
    const [hasUploadResultItems, setHasUploadResultItems] = useState<boolean>(
        false
    )
    const [newImagesUploadCount, setNewImagesUploadCount] = useState<number>(0)
    const [uploadLoading, setUploadLoading] = useState<boolean>(false)
    const [uploadLoadingStarted, setUploadLoadingStarted] = useState<boolean>(
        false
    )
    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [
        getVimeoThumbnailUrl,
        { loading: vimeoLoading, data: vimeoData },
    ] = useVimeoThumnbailUrl()

    // shallow copy and remove empty placeholder

    const [
        mediaDialogConsolidatedProductMedia,
        setMediaDialogConsolidatedProductMedia,
    ] = useState<Record<string, ConsolidatedProductMedia>>({})
    useEffect(() => {
        const initConsolProductMediaCopy = {
            ...initialConsolidatedProductMedia,
        }
        delete initConsolProductMediaCopy['empty']
        setMediaDialogConsolidatedProductMedia(initConsolProductMediaCopy)
    }, [initialConsolidatedProductMedia])
    const mediaDialogConsolidatedProductMediaKeys = Object.keys(
        mediaDialogConsolidatedProductMedia
    )
    const resetOnClose = (event?: any, reason?: string) => {
        if (reason === 'backdropClick') {
            return
        }
        const initConsolProductMediaCopy = {
            ...initialConsolidatedProductMedia,
        }
        delete initConsolProductMediaCopy['empty']
        setMediaDialogConsolidatedProductMedia(initConsolProductMediaCopy)
        onClose()
    }
    const productImages: Record<string, ConsolidatedProductMedia> = {}
    const productVideos: Record<string, ConsolidatedProductMedia> = {}

    mediaDialogConsolidatedProductMediaKeys.forEach((key) => {
        const currentMedia = mediaDialogConsolidatedProductMedia[key]
        if (!!currentMedia.videoId) {
            productVideos[key] = currentMedia
            return
        }

        productImages[key] = currentMedia
    })

    const handleFilestackActive = (active: boolean) => (
        pickerInstance?: filestack.PickerInstance
    ) => {
        if (active && !!pickerInstance)
            setFilestackPickerInstance(pickerInstance)

        setFilestackActive(active)
    }

    const updateMedia = (
        newMedia: Record<string, ConsolidatedProductMedia>
    ) => {
        setMediaDialogConsolidatedProductMedia({
            ...mediaDialogConsolidatedProductMedia,
            ...newMedia,
        })
    }

    const handleDialogSave = () => {
        setInitialConsolidatedProductMedia(mediaDialogConsolidatedProductMedia)
        const imagesInput = Object.keys(productImages).map((each) => ({
            id: productImages[each].id,
            isPrimary: productImages[each].order === 0 ? true : false,
            order: productImages[each].order,
            originalFilename: productImages[each].originalFilename,
            url: productImages[each].originalUrl,
            fileUploadDocument: productImages[each].filename
                ? {
                      fileName: productImages[each].filename,
                      additionalProperties: {
                          resizeRequest: [
                              {
                                  height: 300,
                                  width: 400,
                              },
                              {
                                  height: 600,
                                  width: 800,
                              },
                          ],
                      },
                  }
                : undefined,
        })) as Partial<ListingDetailImageInput>[]
        const videpsInput = Object.keys(productVideos).map((each) => ({
            fromSource: productVideos[each].fromSource,
            id: productVideos[each].id,
            order: productVideos[each].order,
            videoId: productVideos[each].videoId,
            videoStillImageUrl: productVideos[each].thumbnailUrl,
        })) as Partial<ListingDetailVideoInput>[]
        handleMedia(imagesInput, videpsInput)
        onClose()
    }

    const handleProductVideoImage = ({
        productVideoObj,
        removedId,
    }: VideoUpdateProps) => {
        if (!!removedId) {
            const shallowMediaCopy = { ...mediaDialogConsolidatedProductMedia }
            delete shallowMediaCopy[removedId]
            setMediaDialogConsolidatedProductMedia(shallowMediaCopy)
            return
        }

        if (!!productVideoObj) {
            updateMedia(productVideoObj)
        }
    }

    const handleTabChange = (_e: React.ChangeEvent<{}>, newTabValue: number) =>
        setActiveTab(newTabValue)

    // close filestack when trying to wait for the data to be transferred to blob storage
    useEffect(() => {
        if (
            filestackResponseLoading ||
            mediaDialogConsolidatedProductMediaKeys.length === 10
        ) {
            filestackPickerInstance?.close()
            setFilestackPickerInstance(undefined)
        }
    }, [
        filestackResponseLoading,
        filestackPickerInstance,
        mediaDialogConsolidatedProductMediaKeys,
    ])

    // Get thumbnail images for video links
    useEffect(() => {
        mediaDialogConsolidatedProductMediaKeys.forEach((key) => {
            const currentMedia = mediaDialogConsolidatedProductMedia[key]
            if (currentMedia.thumbnailUrlFetching) {
                if (currentMedia.fromSource === 'Vimeo' && !vimeoLoading) {
                    getVimeoThumbnailUrl({
                        mediaId: key,
                        url: currentMedia.originalFilename ?? '',
                    })
                }
            }
        })
    }, [
        mediaDialogConsolidatedProductMediaKeys,
        mediaDialogConsolidatedProductMedia,
        getVimeoThumbnailUrl,
        vimeoLoading,
    ])

    useEffect(() => {
        if (vimeoData && !!vimeoData.mediaId) {
            const currentMediaObj =
                mediaDialogConsolidatedProductMedia[vimeoData.mediaId]

            if (!currentMediaObj || !currentMediaObj.thumbnailUrlFetching)
                return

            setMediaDialogConsolidatedProductMedia({
                ...mediaDialogConsolidatedProductMedia,
                [vimeoData.mediaId]: {
                    ...mediaDialogConsolidatedProductMedia[vimeoData.mediaId],
                    thumbnailUrlFetching: false,
                    thumbnailUrl: vimeoData.thumbnail_url,
                    imgUrl: vimeoData.thumbnail_url,
                },
            })
        }
    }, [vimeoData, mediaDialogConsolidatedProductMedia])

    return (
        <Dialog
            open={open}
            onClose={resetOnClose}
            maxWidth="md"
            fullWidth
            aria-labelledby="uploadMediaDialog"
            className={baseActionButtonClasses.baseButton}
        >
            <DialogTitle id="uploadMediaDialog">
                Upload or add url for your:
                <IconButton
                    aria-label="close"
                    onClick={resetOnClose}
                    className={classes.dialogCloseButton}
                    disabled={uploadLoadingStarted || uploadLoading}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    aria-label="Photos, videos, and gallery tabs"
                >
                    <Tab
                        label="Photos"
                        {...a11yProps(PhotosVideosGalleryEnum.Photos)}
                        className={classes.tabs}
                        disableRipple
                        disabled={uploadLoadingStarted || uploadLoading}
                    />
                    <Tab
                        label="Videos"
                        {...a11yProps(PhotosVideosGalleryEnum.Videos)}
                        className={classes.tabs}
                        disableRipple
                        disabled={uploadLoadingStarted || uploadLoading}
                    />
                    <Tab
                        label="Gallery"
                        {...a11yProps(PhotosVideosGalleryEnum.Gallery)}
                        className={classes.tabs}
                        disableRipple
                        disabled={uploadLoadingStarted || uploadLoading}
                    />
                </Tabs>
                <PhotosVideosGalleryTabPanel
                    index={PhotosVideosGalleryEnum.Photos}
                    value={activeTab}
                >
                    <PhotosTabContent
                        filestackActive={filestackActive}
                        filestackLoading={filestackLoading}
                        filestackResponse={filestackResponse}
                        filestackResponseLoading={filestackResponseLoading}
                        newImagesUploadCount={newImagesUploadCount}
                        onUpdate={updateMedia}
                        setFilestackResponse={setFilestackResponse}
                        setHasUploadResultItems={setHasUploadResultItems}
                        showGlobalSnackbar={showGlobalSnackbar}
                        totalMediaDialogConsolidatedProductMedia={
                            mediaDialogConsolidatedProductMediaKeys.length
                        }
                        uploadLoading={uploadLoading}
                        setUploadLoading={setUploadLoading}
                        activeTab={activeTab === PhotosVideosGalleryEnum.Photos}
                    />
                </PhotosVideosGalleryTabPanel>
                <PhotosVideosGalleryTabPanel
                    index={PhotosVideosGalleryEnum.Videos}
                    value={activeTab}
                >
                    <VideosTabContent
                        totalMediaDialogConsolidatedProductMedia={
                            mediaDialogConsolidatedProductMediaKeys.length
                        }
                        onUpdate={handleProductVideoImage}
                        productVideos={productVideos}
                    />
                </PhotosVideosGalleryTabPanel>
                <PhotosVideosGalleryTabPanel
                    index={PhotosVideosGalleryEnum.Gallery}
                    value={activeTab}
                >
                    <GalleryTabContent
                        mediaDialogConsolidatedProductMedia={
                            mediaDialogConsolidatedProductMedia
                        }
                        mediaDialogConsolidatedProductMediaKeys={
                            mediaDialogConsolidatedProductMediaKeys
                        }
                        setMediaDialogConsolidatedProductMedia={
                            setMediaDialogConsolidatedProductMedia
                        }
                    />
                </PhotosVideosGalleryTabPanel>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                {mediaDialogConsolidatedProductMediaKeys.length < 10 && (
                    <ImageFilestackButton
                        apiKey={processEnv.REACT_APP_FileStack_ApiKey}
                        kpCoreUri={processEnv.REACT_APP_KPCoreUri}
                        cropDimension={[800, 600]}
                        showGlobalSnackbar={showGlobalSnackbar}
                        onFilepickerDone={setFilestackResponse}
                        handleLoadingFilestack={(loading: boolean) => {
                            setFilestackLoading(loading)
                            setUploadLoadingStarted(loading)
                        }}
                        handleTransferringFiles={setFilestackResponseLoading}
                        pickerOptions={{
                            displayMode: filestack.PickerDisplayMode.inline,
                            container: `#${photosFilestackDivId}`,
                            onOpen: handleFilestackActive(true),
                            onClose: handleFilestackActive(false),
                            onUploadDone: (pickerResponse: PickerResponse) => {
                                setNewImagesUploadCount(
                                    pickerResponse.filesUploaded.length
                                )
                                setUploadLoadingStarted(false)
                            },
                            onFileUploadStarted: () =>
                                setUploadLoadingStarted(true),
                        }}
                        maxFiles={
                            10 - mediaDialogConsolidatedProductMediaKeys.length
                        }
                        openPicker={
                            PhotosVideosGalleryEnum.Photos === activeTab
                        }
                        renderButton={(
                            onClick: (event: React.MouseEvent) => void
                        ) => (
                            <ActionButton
                                onClick={onClick}
                                hidden={
                                    !hasUploadResultItems ||
                                    activeTab !==
                                        PhotosVideosGalleryEnum.Photos ||
                                    uploadLoading
                                }
                            >
                                Add More Photos
                            </ActionButton>
                        )}
                    />
                )}
                <ActionButton
                    onClick={handleDialogSave}
                    disabled={uploadLoadingStarted || uploadLoading}
                >
                    Save
                </ActionButton>
            </DialogActions>
        </Dialog>
    )
}
